// ============================================================
//  ADMIN-LOGIN — passwortloser Email-Link-Login
//
//  Ablauf:
//  1. User trägt E-Mail ein → sendSignInLinkToEmail()
//     (nur relevant falls Rico den Link manuell nochmal anfordert;
//     der normale Weg ist der Link aus der Einladungsmail von
//     der Cloud Function "sendInvite").
//  2. User klickt Link → landet wieder hier → isSignInWithEmailLink
//     erkennt das → signInWithEmailLink() → acceptInvite() Cloud
//     Function → Custom Claim gesetzt → Redirect ins Admin-Dashboard.
// ============================================================
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase";
import { useAuth } from "../../cms/AuthContext";

const EMAIL_STORAGE_KEY = "tss-cms-email-fuer-login";

export default function Login() {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | completing | error
  const [fehler, setFehler] = useState("");

  // Bereits eingeloggt? Direkt weiter ins Dashboard.
  useEffect(() => {
    if (!authLoading && user && role) {
      navigate("/admin", { replace: true });
    }
  }, [authLoading, user, role, navigate]);

  // Login-Link aus der E-Mail abschliessen
  useEffect(() => {
    async function linkAbschliessen() {
      if (!isSignInWithEmailLink(auth, window.location.href)) return;

      setStatus("completing");
      let gespeicherteEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY);

      if (!gespeicherteEmail) {
        gespeicherteEmail = window.prompt(
          "Bitte bestätige deine E-Mail-Adresse für den Login:"
        );
      }
      if (!gespeicherteEmail) {
        setStatus("error");
        setFehler("E-Mail-Adresse wird für den Login benötigt.");
        return;
      }

      try {
        await signInWithEmailLink(auth, gespeicherteEmail, window.location.href);
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);

        const acceptInvite = httpsCallable(functions, "acceptInvite");
        await acceptInvite();

        // Custom Claim wurde gerade gesetzt — Token neu holen.
        await auth.currentUser.getIdToken(true);

        navigate("/admin", { replace: true });
      } catch (err) {
        setStatus("error");
        setFehler(err.message || "Login fehlgeschlagen.");
      }
    }
    linkAbschliessen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    setFehler("");
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.origin + "/admin/login",
        handleCodeInApp: true,
      });
      window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setFehler(err.message || "Senden fehlgeschlagen.");
    }
  }

  if (status === "completing") {
    return (
      <div className="mx-auto max-w-md px-4 py-32 text-center">
        <p className="text-dark/70">Login wird abgeschlossen …</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-24">
      <h1 className="text-2xl font-extrabold uppercase mb-6 text-primary">
        Redaktions-Login
      </h1>

      {status === "sent" ? (
        <p className="text-dark/80">
          Login-Link an <strong>{email}</strong> geschickt. E-Mail öffnen und
          draufklicken.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.ch"
            className="w-full border border-dark/20 px-4 py-3"
          />
          <button
            type="submit"
            disabled={status === "sending"}
            className="w-full bg-primary text-white font-bold uppercase py-3 hover:bg-accent transition-colors disabled:opacity-50"
          >
            {status === "sending" ? "Sende Link …" : "Login-Link anfordern"}
          </button>
        </form>
      )}

      {fehler && <p className="mt-4 text-sm text-red-600">{fehler}</p>}
    </div>
  );
}
